/**
 * initObservability — wires Sentry crash reporting and PostHog analytics for
 * the whole app. Called once from `main.tsx`; both SDKs are dynamically
 * imported so a missing env var costs nothing at bundle time.
 *
 * Shell: any (called before the router mounts, so it runs regardless of
 * which shell the current URL resolves to).
 *
 * ## Scrubbing strategy: by secret SHAPE, not by field/route name
 *
 * `/invite/:token` puts a genuine secret in a URL **path segment** — a
 * single-use invite token — and `/reset-password` puts a different secret
 * (a Supabase recovery token) in the URL **hash**. Sentry's default
 * `httpContextIntegration` puts `document.location.href` on every event's
 * `request.url` *and* `document.referrer` on `request.headers.Referer`;
 * PostHog autocaptures a growing set of URL/pathname-shaped properties on
 * every event. Either secret would otherwise ship to a third party.
 *
 * The query string and hash are dropped **unconditionally**, for every
 * route and every field this module touches — that rule isn't
 * content-dependent, so it needs no enumeration, and it covers
 * `/reset-password`'s hash-delivered recovery token and any `?email=`-
 * shaped PII the same way regardless of which field carries it.
 *
 * The **path** can't be dropped the same blanket way (the path is the part
 * of the URL that's actually useful for grouping errors/events), so a
 * path-shaped secret has to be found and redacted in place instead. This
 * module tried, twice, to do that by **naming every field** PostHog puts a
 * URL/pathname in, and both attempts shipped incomplete:
 *
 * - round 3 scrubbed `$current_url`/`$referrer`/`$initial_current_url`/
 *   `$initial_referrer` but not `$pathname` itself.
 * - round 4 added `$pathname`, but missed `$set_once` entirely — it's a
 *   **sibling** of `properties` on PostHog's `CaptureResult`
 *   (`{ uuid, event, properties, $set, $set_once, timestamp }`,
 *   `@posthog/types/dist/capture.d.ts`), not a member of it, so
 *   `$initial_current_url`/`$initial_referrer` were being scrubbed at a
 *   location PostHog never writes them to. It also missed
 *   `$session_entry_url`/`$session_entry_pathname` (present on `properties`
 *   for the life of the session, no `identify()` required) and
 *   `$prev_pageview_pathname` (delivered on the `$pageleave` event, which
 *   fires on tab unload — even after the SPA has already navigated away
 *   from `/invite/:token`).
 *
 * Three rounds, three different missed fields (tracked as #656 and its
 * follow-on #663). **This module no longer enumerates PostHog property
 * names at all.** {@link posthogBeforeSend} walks the *entire*
 * `CaptureResult` recursively via {@link scrubDeep} — `properties`, `$set`,
 * `$set_once`, and any further top-level field a future posthog-js version
 * adds — and scrubs every string leaf it finds. That closes the whole
 * *class* of bug rather than the one instance of it a reviewer happened to
 * catch: a fourth PostHog property carrying a URL needs no code change
 * here.
 *
 * The redaction itself is **shape-based, not route-based**, for the same
 * reason: {@link SECRET_TOKEN_PATTERNS} matches the invite token's own
 * content — `create_account_invite()` returns 32 random bytes, hex-encoded
 * (`encode(gen_random_bytes(32), 'hex')`,
 * `supabase/migrations/20260806121000_seat_write_gate.sql`) — 64 lowercase
 * hex characters, wherever that substring appears in a string, rather than
 * requiring the scrubber to know the secret lives specifically at
 * `/invite/:token`. The previous route-name approach (`/^\/invite\//`) also
 * has to track React Router's own case-insensitive matching (`/Invite/
 * <token>` still resolves to the same route) or it under-matches; shape
 * matching sidesteps that — there is no route name whose case needs
 * tracking.
 *
 * ## The one request `before_send` never sees (#664)
 *
 * Everything above is about **captured events**. PostHog's feature-flag
 * request is not one — `/flags` is issued from the SDK's own request path and
 * never passes through `before_send`, while carrying
 * `person_properties: {...get_initial_props()}`, which expands
 * `$initial_person_info` into `$initial_current_url` and `$initial_referrer`,
 * path included. That is the same secret this module exists to keep out of
 * third-party hands, on a request the scrubber cannot reach.
 *
 * So it is closed at the source instead: `advanced_disable_flags: true` in
 * {@link initObservability} stops the request being made. See the comment
 * there for why the similarly-named `advanced_disable_feature_flags` is not
 * the same option and does not help.
 *
 * PostHog session replay is explicitly turned off via
 * `disable_session_recording: true` rather than left to whatever the SDK
 * defaults to — the same "don't rely on a default" posture the Sentry config
 * below already takes with `replaysSessionSampleRate: 0`.
 */
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react';
import type { BeforeSendFn, CaptureResult } from 'posthog-js';

/**
 * Genuine secrets this app ever puts in a URL, matched by their own content
 * shape rather than by which route or field carries them.
 *
 * - the invite-accept token from `create_account_invite()`: 32 random
 *   bytes, hex-encoded — 64 lowercase hex characters (see the module
 *   docblock for the exact source). `g` (not anchored): has to match as a
 *   substring inside a larger path/URL/property-value string, never the
 *   whole string. `i`: defense-in-depth against a future encoding path that
 *   capitalizes hex digits — `encode(bytes, 'hex')` itself is always
 *   lowercase today.
 *
 * A future route that puts a differently-shaped secret in a URL adds one
 * more pattern here — this array, not {@link scrubUrl}'s or
 * {@link scrubDeep}'s callers, is the one place that needs updating.
 */
const SECRET_TOKEN_PATTERNS: readonly RegExp[] = [/[0-9a-f]{64}/gi];

/** Replaces every occurrence of every {@link SECRET_TOKEN_PATTERNS} match in `value` with `[redacted]`. */
function redactSecretTokens(value: string): string {
  let result = value;
  for (const pattern of SECRET_TOKEN_PATTERNS) {
    result = result.replace(pattern, '[redacted]');
  }
  return result;
}

/**
 * Scrubs a URL-shaped string (an absolute URL, or a path resolved against
 * `window.location.origin`): drops the query string and hash
 * unconditionally, then redacts any {@link SECRET_TOKEN_PATTERNS} match
 * remaining in the path. Returns `raw` run through {@link redactSecretTokens}
 * unchanged if it isn't parseable as a URL at all — a malformed "URL" can
 * still contain the literal token, so token redaction still runs on it.
 *
 * Used for fields that are themselves full URLs — `event.request.url`,
 * `event.request.headers.Referer`, Sentry breadcrumb `url`/`to`/`from`, and
 * (via {@link scrubDeep}) every URL-shaped string PostHog's `CaptureResult`
 * carries.
 */
export function scrubUrl(raw: string): string {
  try {
    const url = new URL(raw, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = redactSecretTokens(url.pathname);
    return url.toString();
  } catch {
    return redactSecretTokens(raw);
  }
}

/**
 * Same redaction as {@link scrubUrl}, but for a bare pathname rather than a
 * full URL — PostHog's `$pathname`/`$session_entry_pathname`/
 * `$prev_pageview_pathname`/`$initial_pathname` properties are only ever a
 * path (`location.pathname`), never a full `https://…` string, so scrubbing
 * one through {@link scrubUrl} would invent an origin that was never in the
 * original value. Returns `raw` run through {@link redactSecretTokens}
 * unchanged if it isn't parseable at all.
 */
export function scrubPathname(raw: string): string {
  try {
    const url = new URL(raw, window.location.origin);
    return redactSecretTokens(url.pathname);
  } catch {
    return redactSecretTokens(raw);
  }
}

const BREADCRUMB_URL_KEYS = ['url', 'to', 'from'] as const;

/**
 * Sentry `beforeSend` — scrubs every URL-shaped field on an outgoing error
 * event: `request.url`, the `Referer` request header the default
 * `httpContextIntegration` populates from `document.referrer`, and drops
 * `request.query_string` entirely (redundant with the scrubbed `url`, and
 * PII-shaped params like `?email=` shouldn't survive as a separate field
 * either way). Sentry's `ErrorEvent` shape is fixed by the SDK's own types
 * (unlike PostHog's `CaptureResult`, where an enumerated-field approach kept
 * missing autocaptured properties — see the module docblock), so this
 * stays field-by-field rather than a recursive walk. Exported so it's
 * exercised directly by `__tests__/observability.test.ts` without booting
 * the real Sentry SDK.
 */
export function sentryBeforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (event.request?.query_string) {
    delete event.request.query_string;
  }
  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
  }
  return event;
}

/** Sentry `beforeBreadcrumb` — scrubs the same URL-shaped keys on navigation breadcrumbs. */
export function sentryBeforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (!breadcrumb.data) return breadcrumb;
  const data = { ...breadcrumb.data };
  for (const key of BREADCRUMB_URL_KEYS) {
    const value: unknown = data[key];
    if (typeof value === 'string') data[key] = scrubUrl(value);
  }
  return { ...breadcrumb, data };
}

/**
 * How many levels deep {@link scrubDeep} will recurse into a `CaptureResult`
 * before giving up and returning a value unscrubbed. Defense-in-depth only
 * — posthog-js's own event-building already truncates and cycle-guards
 * (a `Set` of visited references in its internal serializer) everything
 * passed to `before_send`, so this cap should never actually bind; it just
 * keeps {@link scrubDeep} from being the thing that turns a future SDK
 * change into a stack overflow.
 */
const MAX_SCRUB_DEPTH = 12;

/** True for a plain `{}`-literal-shaped object — false for `null`, arrays,
 * `Date`, and anything else with a non-`Object.prototype` prototype, so
 * {@link scrubDeep} walks into the former and leaves the latter alone. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** A string starting with `/` or `https?://` gets the full URL treatment
 * (query/hash dropped unconditionally, path token-redacted); every other
 * string still gets {@link redactSecretTokens} applied directly — a secret
 * doesn't have to live inside something URL-shaped to leak. */
function scrubStringLeaf(raw: string): string {
  if (/^https?:\/\//i.test(raw)) return scrubUrl(raw);
  if (raw.startsWith('/')) return scrubPathname(raw);
  return redactSecretTokens(raw);
}

/**
 * Recursively walks an arbitrary value — a PostHog `CaptureResult`, or any
 * of its nested `properties`/`$set`/`$set_once` objects — and redacts every
 * string leaf via {@link scrubStringLeaf}. Arrays are walked element-wise;
 * plain objects are rebuilt key-for-key with each value scrubbed; anything
 * else (numbers, booleans, `null`, `undefined`, `Date`, and any other
 * non-plain object) passes through **unchanged** rather than being walked —
 * without that guard a `timestamp: Date` field would be rebuilt into `{}`
 * by `Object.entries`, since a `Date` instance has no own enumerable
 * properties.
 *
 * This replaces an enumerated list of PostHog property names
 * (`$current_url`, `$pathname`, …) that was wrong on three separate review
 * rounds (#656, #663) because posthog-js keeps adding autocaptured
 * properties, and keeps choosing where on the `CaptureResult` to put them,
 * that this module didn't know to look for. Walking every string in the
 * object regardless of which key it's under closes the whole class of
 * "which field does this SDK version use" bug, not just the specific
 * instance a reviewer happened to find.
 */
function scrubDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    return scrubStringLeaf(value);
  }
  if (depth >= MAX_SCRUB_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item: unknown) => scrubDeep(item, depth + 1));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    out[key] = scrubDeep(val, depth + 1);
  }
  return out;
}

/**
 * PostHog `before_send` — recursively scrubs every string in the whole
 * `CaptureResult` (`properties`, `$set`, `$set_once`, and any other
 * top-level field a future posthog-js version adds) via {@link scrubDeep}.
 * See the module docblock for why this replaced an enumerated
 * property-key allow-list.
 */
export const posthogBeforeSend: BeforeSendFn = (cr: CaptureResult | null) => {
  if (!cr) return cr;
  // scrubDeep only ever replaces string leaves and rebuilds plain
  // objects/arrays with the same keys and shape — it never adds, removes,
  // or reorders a key — so its output is still a CaptureResult. This cast
  // is over the output of that guaranteed-shape-preserving transform, not
  // over unvalidated external input.
  return scrubDeep(cr) as CaptureResult;
};

let initialized = false;

export function initObservability(): void {
  if (initialized) return;
  initialized = true;

  const sentryDsn = import.meta.env.VENDOR_SENTRY_DSN;
  const posthogKey = import.meta.env.VENDOR_POSTHOG_KEY;
  const posthogHost = import.meta.env.VENDOR_POSTHOG_HOST ?? 'https://us.i.posthog.com';

  if (!sentryDsn) {
    console.warn('[vendor] VENDOR_SENTRY_DSN not set — crash reporting disabled');
  } else {
    void import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: sentryBeforeSend,
        beforeBreadcrumb: sentryBeforeBreadcrumb,
      });
    });
  }

  if (!posthogKey) {
    console.warn('[vendor] VENDOR_POSTHOG_KEY not set — analytics disabled');
  } else {
    void import('posthog-js').then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        // Explicit, not relied-on-as-a-default: session replay stays off
        // until it's a deliberate product decision, same posture as
        // `replaysSessionSampleRate: 0` above for Sentry.
        disable_session_recording: true,
        // vendor#664. `before_send` is a CAPTURED-EVENT hook; the /flags
        // request is not a captured event, so nothing above scrubs it.
        // Confirmed against the shipped posthog-js 1.374.2 bundle rather than
        // reasoned from the docs: the flags payload is built as
        // `{ token, distinct_id, groups, $anon_distinct_id, person_properties:
        // {...get_initial_props(), ...stored}, group_properties, timezone }`
        // and sent through the raw request path, never through
        // `_runBeforeSend`. `get_initial_props()` expands
        // `$initial_person_info` into `$initial_current_url` /
        // `$initial_referrer` — the **path** included. So a user who lands on
        // `/invite/<64-hex-token>` has that token persisted and replayed to
        // PostHog on every subsequent flags request, outside the scrubber that
        // #656/#663 spent three rounds getting right.
        //
        // `advanced_disable_flags` is the option that stops the request; the
        // similarly-named `advanced_disable_feature_flags` does NOT — it keeps
        // /flags running and merely adds `disable_flags: true` to the same
        // payload, `person_properties` and all. Verified in
        // `@posthog/types@1.374.2` `posthog-config.d.ts`.
        //
        // Safe here because this app uses **zero** feature flags — no
        // `isFeatureEnabled`, `getFeatureFlag`, `onFeatureFlags` or
        // `useFeatureFlag` call site exists. The documented cost is that
        // remote configuration no longer loads, so anything toggled in the
        // PostHog project UI (surveys, web vitals, autocapture off) stops
        // reaching this client and the client-side config here becomes the
        // whole truth. That is the posture this module already takes twice
        // over with `disable_session_recording` and `replaysSessionSampleRate`.
        // The day vendor wants a feature flag, this line comes out and the
        // flags payload needs its own scrubbing story — `before_send` will
        // still not be it.
        advanced_disable_flags: true,
        before_send: posthogBeforeSend,
      });
    });
  }
}
